import type {
  FileAttachment,
  Message,
  ZulipConfig,
  ZulipMessage,
  ZulipMessageEvent,
  ZulipMessagesResponse,
} from '@/utils/zulip/types';

import { Ref, UnwrapRef, ref } from 'vue';

import { ZulipApi } from '@/utils/zulip/api';

export function useZulip(
  config: ZulipConfig,
  streamName: string,
  topic: string,
): {
  sendMessage: (
    content: string,
    files?: File | File[],
  ) => Promise<ZulipMessagesResponse | null>;
  getMessages: (params: {
    anchor: 'newest' | 'oldest' | number;
    numBefore: number;
    numAfter: number;
    narrow?: object[];
  }) => Promise<ZulipMessageEvent[]>;
  initialize: () => Promise<void>;
  cleanup: () => void;
  loadMessages: (streamName: string, topic: string) => Promise<void>;
  error: Ref<UnwrapRef<Error | null>, UnwrapRef<Error | null> | Error | null>;
  isLoading: Ref<UnwrapRef<boolean>, UnwrapRef<boolean> | boolean>;
  messages: Ref<UnwrapRef<Message[]>, UnwrapRef<Message[]> | Message[]>;
} {
  const zulipClient = new ZulipApi(config);
  const error = ref<Error | null>(null);
  const isLoading = ref(false);
  const messages = ref<Message[]>([]);
  const pollingInterval = ref<number | null>(null);

  const sendMessage = async (
    content: string,
    files?: File | File[],
  ): Promise<ZulipMessagesResponse | null> => {
    error.value = null;
    isLoading.value = true;

    const message: ZulipMessage = {
      type: 'stream',
      to: streamName,
      content,
      topic,
      attachments: files,
    };

    try {
      const response = await zulipClient.sendMessage(message);
      return response;
    } catch (e) {
      error.value =
        e instanceof Error ? e : new Error('Unknown error occurred');
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Generic method to get messages from the Zulip server.
   */
  const getMessages = async (params: {
    anchor: 'newest' | 'oldest' | number;
    numBefore: number;
    numAfter: number;
    narrow?: object[];
    apply_markdown?: boolean;
  }): Promise<ZulipMessageEvent[]> => {
    error.value = null;
    // isLoading.value = true;

    try {
      const response = await zulipClient.getMessages(params);
      return response.messages;
    } catch (e) {
      error.value =
        e instanceof Error ? e : new Error('Unknown error occurred');
      return [];
    } finally {
      // isLoading.value = false;
    }
  };

  /**
   * Load messages from the Zulip server and update the `messages` ref.
   * Attachments are extracted from the message content and added to the message object.
   */
  const loadMessages = async (
    streamName: string,
    topic: string,
  ): Promise<void> => {
    const narrow = [
      { operator: 'channel', operand: streamName },
      { operator: 'topic', operand: topic },
    ];
    const fetchedMessages = await getMessages({
      anchor: 'newest',
      numBefore: 50,
      numAfter: 0,
      narrow,
      apply_markdown: true,
    });

    // remove inline images
    const regex = /<div class="message_inline_image">.*<\/div>/g;
    fetchedMessages.forEach((msg: ZulipMessageEvent) => {
      msg.content = msg.content.replace(regex, '');
    });

    // remove spoiler blocks. Regexp not working here as div has multiple nested divs
    fetchedMessages.forEach((msg: ZulipMessageEvent) => {
      const document = new DOMParser().parseFromString(
        msg.content,
        'text/html',
      );
      const spoilerBlocks = document.querySelectorAll('.spoiler-block');
      spoilerBlocks.forEach((block) => {
        block.remove();
      });
      msg.content = document.body.innerHTML;
    });

    // Need to find user_uploads links and extract them
    // and add them to the message object
    // TODO: get public URL for attachments
    // Markdown link regex
    // const regex = /\[([^\]]+)]\(([^)]+)\)/g;
    const attachmentRegex = /<a href="user_uploads\/2\/\d+\/[^"]+">[^<]+<\/a>/g;
    messages.value = fetchedMessages.map((msg: ZulipMessageEvent) => {
      const attachments: FileAttachment[] = [];
      const matches = msg.content.match(attachmentRegex);
      if (matches) {
        matches.forEach((match) => {
          const [url, name] = match
            .replace('<a href="', '')
            .replace('">', '|')
            .replace('</a>', '')
            .split('|');
          attachments.push({ name, url, size: 0 });
        });
      }
      return {
        content: msg.content,
        sender: msg.sender_full_name,
        timestamp: msg.timestamp,
        attachments: attachments.length > 0 ? attachments : undefined,
      };
    });
  };

  /**
   * Initialize the chat by loading messages
   * and setting up polling for new messages.
   */
  const initialize = async (): Promise<void> => {
    try {
      await loadMessages(streamName, topic);
      pollingInterval.value = window.setInterval(() => {
        void (async () => {
          await loadMessages(streamName, topic);
        })();
      }, 5000);
    } catch (err) {
      error.value = err as Error;
    }
  };

  const cleanup = (): void => {
    if (pollingInterval.value) {
      clearInterval(pollingInterval.value);
    }
  };

  return {
    sendMessage,
    getMessages,
    initialize,
    cleanup,
    loadMessages,
    error,
    isLoading,
    messages,
  };
}
